import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../services/api';

// Painel de diagnóstico de TODA geração de IA do produto (texto, imagem, vídeo,
// áudio/SEO), do Marketing Studio aos pipelines de e-commerce. Mostra o que
// falhou, em qual plataforma, quanto tempo esperou e quantas tentativas
// automáticas aconteceram antes de o erro chegar (ou não) ao usuário.

interface LogRow {
  id: string;
  tenantId: string | null;
  tenantName: string | null;
  surface: string;
  kind: string;
  provider: string;
  model: string | null;
  operation: string;
  label: string | null;
  jobId: string | null;
  jobType: string | null;
  stepId: string | null;
  status: string;
  attempts: number;
  maxAttempts: number;
  timeoutMs: number | null;
  totalMs: number | null;
  providerMs: number | null;
  waitMs: number | null;
  cache?: { inputTokens: number; cachedTokens: number; hitRatio: number } | null;
  httpStatus: number | null;
  retryable: boolean | null;
  errorKind: string | null;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
}

interface AttemptEntry {
  n: number;
  startedAt: string;
  durationMs: number;
  status: string;
  errorKind?: string;
  retryable?: boolean;
  httpStatus?: number | null;
  error?: string;
  waitAfterMs?: number;
  note?: string;
}

const SURFACE_LABELS: Record<string, string> = {
  marketing_studio: 'Marketing Studio',
  marketing_video: 'Marketing Studio — Vídeo',
  ecommerce_photo: 'E-commerce — Fotos',
  ecommerce_video: 'E-commerce — Vídeos',
  seller: 'Seller',
  outros: 'Outros',
};

const PROVIDER_LABELS: Record<string, string> = {
  omniroute_chat: 'OmniRoute · Chat',
  omniroute_responses: 'OmniRoute · Responses',
  omniroute_image: 'OmniRoute · Imagem',
  omniroute_video: 'OmniRoute · Vídeo',
  heygen: 'HeyGen',
  snapgen: 'SnapGen',
  fal: 'fal.ai',
  kie: 'KIE',
  runway: 'Runway',
  groq: 'Groq',
};

const ERROR_KIND_LABELS: Record<string, string> = {
  timeout: 'Timeout do provedor',
  rate_limit: 'Limite de uso (429)',
  http_5xx: 'Erro do provedor (5xx)',
  network: 'Rede',
  no_output: 'Sem resultado',
  invalid_response: 'Resposta inválida',
  content_policy: 'Política de conteúdo',
  auth: 'Credencial',
  validation: 'Requisição rejeitada',
  credits: 'Sem créditos',
  interrupted: 'Interrompido (restart)',
  unknown: 'Desconhecido',
};

const KIND_ICONS: Record<string, string> = {
  text: '📝', image: '🖼️', video: '🎬', audio: '🔊', seo: '🔍',
};

function labelFor(map: Record<string, string>, value: string | null | undefined) {
  if (!value) return '—';
  return map[value] || value;
}

function formatDuration(ms: number | null | undefined) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m${String(rest).padStart(2, '0')}s`;
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    success: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    running: 'bg-blue-100 text-blue-800 animate-pulse',
    canceled: 'bg-gray-100 text-gray-700',
  };
  const labels: Record<string, string> = {
    success: 'Sucesso', failed: 'Falhou', running: 'Executando', canceled: 'Cancelada',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {labels[status] || status}
    </span>
  );
}

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'ok' | 'bad' | 'warn' }) {
  const toneClass = tone === 'bad' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : tone === 'ok' ? 'text-green-600' : 'text-gray-900';
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
}

export function GenerationLogsManager() {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [hours, setHours] = useState('24');
  const [status, setStatus] = useState('all');
  const [surface, setSurface] = useState('all');
  const [provider, setProvider] = useState('all');
  const [kind, setKind] = useState('all');
  const [errorKind, setErrorKind] = useState('all');
  const [tenantId, setTenantId] = useState('all');
  const [retriedOnly, setRetriedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const query = useMemo(() => ({
    hours, status, surface, provider, kind, errorKind, tenantId,
    retriedOnly: retriedOnly ? 'true' : undefined,
    search: appliedSearch || undefined,
  }), [hours, status, surface, provider, kind, errorKind, tenantId, retriedOnly, appliedSearch]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.getGenerationLogs({ ...query, limit, offset }),
        api.getGenerationLogStats(query),
      ]);
      if (listRes.success) {
        setRows(listRes.data.items || []);
        setTotal(listRes.data.total || 0);
      }
      if (statsRes.success) setStats(statsRes.data);
    } catch (e: any) {
      if (!opts?.silent) toast.error(e?.message || 'Erro ao carregar logs de geração');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [query, offset]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getGenerationLogFilters()
      .then(res => { if (res.success) setFilterOptions(res.data); })
      .catch(() => { /* filtros são um refinamento opcional */ });
  }, []);

  // Gerações em andamento levam minutos: o auto-refresh é o modo natural de
  // acompanhar um job rodando agora sem ficar clicando em Atualizar.
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => load({ silent: true }), 10000);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  // Voltar para a primeira página quando qualquer filtro muda, senão a lista
  // aparece vazia por causa de um offset herdado.
  useEffect(() => { setOffset(0); }, [hours, status, surface, provider, kind, errorKind, tenantId, retriedOnly, appliedSearch]);

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const res = await api.getGenerationLogDetail(id);
      if (res.success) setDetail(res.data);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao carregar o detalhe do log');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const runMaintenance = async () => {
    try {
      const res = await api.runGenerationLogMaintenance();
      if (res.success) {
        toast.success(`Manutenção concluída: ${res.data.reconciled} órfão(s) fechado(s), ${res.data.purged} antigo(s) removido(s).`);
        load();
      }
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao executar manutenção');
    }
  };

  const selectClass = 'border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white';

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <select className={selectClass} value={hours} onChange={e => setHours(e.target.value)}>
            <option value="1">Última hora</option>
            <option value="6">Últimas 6h</option>
            <option value="24">Últimas 24h</option>
            <option value="72">Últimos 3 dias</option>
            <option value="168">Últimos 7 dias</option>
            <option value="720">Últimos 30 dias</option>
            <option value="0">Todo o período</option>
          </select>

          <select className={selectClass} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="all">Todos os status</option>
            <option value="failed">Só falhas</option>
            <option value="success">Só sucessos</option>
            <option value="running">Em execução</option>
          </select>

          <select className={selectClass} value={surface} onChange={e => setSurface(e.target.value)}>
            <option value="all">Todas as áreas</option>
            {(filterOptions?.surfaces || []).map((opt: any) => (
              <option key={opt.value} value={opt.value}>{labelFor(SURFACE_LABELS, opt.value)} ({opt.count})</option>
            ))}
          </select>

          <select className={selectClass} value={provider} onChange={e => setProvider(e.target.value)}>
            <option value="all">Todas as plataformas</option>
            {(filterOptions?.providers || []).map((opt: any) => (
              <option key={opt.value} value={opt.value}>{labelFor(PROVIDER_LABELS, opt.value)} ({opt.count})</option>
            ))}
          </select>

          <select className={selectClass} value={kind} onChange={e => setKind(e.target.value)}>
            <option value="all">Todos os tipos</option>
            {(filterOptions?.kinds || []).map((opt: any) => (
              <option key={opt.value} value={opt.value}>{KIND_ICONS[opt.value] || ''} {opt.value} ({opt.count})</option>
            ))}
          </select>

          <select className={selectClass} value={errorKind} onChange={e => setErrorKind(e.target.value)}>
            <option value="all">Todos os erros</option>
            {(filterOptions?.errorKinds || []).map((opt: any) => (
              <option key={opt.value} value={opt.value}>{labelFor(ERROR_KIND_LABELS, opt.value)} ({opt.count})</option>
            ))}
          </select>

          <select className={selectClass} value={tenantId} onChange={e => setTenantId(e.target.value)}>
            <option value="all">Todas as empresas</option>
            {(filterOptions?.tenants || []).map((opt: any) => (
              <option key={opt.value} value={opt.value}>{opt.label} ({opt.count})</option>
            ))}
          </select>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={retriedOnly} onChange={e => setRetriedOnly(e.target.checked)} />
            Só com retry
          </label>

          <form
            className="flex items-center gap-2"
            onSubmit={e => { e.preventDefault(); setAppliedSearch(search.trim()); }}
          >
            <input
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
              placeholder="Buscar erro, etapa, modelo, jobId…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button type="submit" className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Buscar</button>
          </form>

          <div className="ml-auto flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
              Auto (10s)
            </label>
            <button onClick={() => load()} className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Atualizar
            </button>
            <button onClick={runMaintenance} className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg" title="Fecha logs órfãos de restart e aplica a retenção">
              Manutenção
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Kpi label="Gerações" value={String(stats.totals.all)} />
          <Kpi label="Sucesso" value={String(stats.totals.success)} tone="ok"
            hint={stats.totals.all ? `${((stats.totals.success / stats.totals.all) * 100).toFixed(1)}%` : undefined} />
          <Kpi label="Falhas" value={String(stats.totals.failed)} tone={stats.totals.failed ? 'bad' : undefined}
            hint={stats.totals.all ? `${((stats.totals.failed / stats.totals.all) * 100).toFixed(1)}%` : undefined} />
          <Kpi label="Em execução" value={String(stats.totals.running)} />
          <Kpi label="Tempo médio" value={formatDuration(stats.totals.avgTotalMs)} hint={`pico ${formatDuration(stats.totals.maxTotalMs)}`} />
          <Kpi label="Tentativas médias" value={String(stats.totals.avgAttempts)}
            tone={stats.totals.avgAttempts > 1.5 ? 'warn' : undefined} hint="1,00 = provedor estável" />
          <Kpi label="Espera em retry" value={formatDuration(stats.totals.avgWaitMs)} hint="média por geração" />
        </div>
      )}

      {/* Resumo por plataforma e por área */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow lg:col-span-2">
            <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900 text-sm">Por plataforma</div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Plataforma</th>
                  <th className="text-right px-3 py-2 font-medium">Total</th>
                  <th className="text-right px-3 py-2 font-medium">Sucesso</th>
                  <th className="text-right px-3 py-2 font-medium">Falhas</th>
                  <th className="text-right px-3 py-2 font-medium">Taxa</th>
                  <th className="text-right px-3 py-2 font-medium">Tempo médio</th>
                  <th className="text-right px-4 py-2 font-medium">Tentativas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.byProvider.map((row: any) => (
                  <tr key={row.name}>
                    <td className="px-4 py-2 text-gray-900">{labelFor(PROVIDER_LABELS, row.name)}</td>
                    <td className="px-3 py-2 text-right">{row.total}</td>
                    <td className="px-3 py-2 text-right text-green-700">{row.success}</td>
                    <td className={`px-3 py-2 text-right ${row.failed ? 'text-red-700 font-medium' : ''}`}>{row.failed}</td>
                    <td className={`px-3 py-2 text-right ${row.successRate < 90 ? 'text-red-700 font-medium' : 'text-gray-700'}`}>{row.successRate}%</td>
                    <td className="px-3 py-2 text-right text-gray-700">{formatDuration(row.avgTotalMs)}</td>
                    <td className={`px-4 py-2 text-right ${row.avgAttempts > 1.5 ? 'text-amber-700 font-medium' : 'text-gray-700'}`}>{row.avgAttempts}</td>
                  </tr>
                ))}
                {!stats.byProvider.length && (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">Nenhuma geração no período.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow lg:col-span-2 lg:order-3">
            <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900 text-sm">Por área do produto</div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Área</th>
                  <th className="text-right px-3 py-2 font-medium">Total</th>
                  <th className="text-right px-3 py-2 font-medium">Sucesso</th>
                  <th className="text-right px-3 py-2 font-medium">Falhas</th>
                  <th className="text-right px-3 py-2 font-medium">Taxa</th>
                  <th className="text-right px-4 py-2 font-medium">Tempo médio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.bySurface.map((row: any) => (
                  <tr key={row.name}>
                    <td className="px-4 py-2 text-gray-900">{labelFor(SURFACE_LABELS, row.name)}</td>
                    <td className="px-3 py-2 text-right">{row.total}</td>
                    <td className="px-3 py-2 text-right text-green-700">{row.success}</td>
                    <td className={`px-3 py-2 text-right ${row.failed ? 'text-red-700 font-medium' : ''}`}>{row.failed}</td>
                    <td className={`px-3 py-2 text-right ${row.successRate < 90 ? 'text-red-700 font-medium' : 'text-gray-700'}`}>{row.successRate}%</td>
                    <td className="px-4 py-2 text-right text-gray-700">{formatDuration(row.avgTotalMs)}</td>
                  </tr>
                ))}
                {!stats.bySurface.length && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Nenhuma geração no período.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow lg:order-2">
            <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900 text-sm">Falhas por causa</div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {stats.byErrorKind.map((row: any) => (
                  <tr key={row.errorKind}>
                    <td className="px-4 py-2 text-gray-900">{labelFor(ERROR_KIND_LABELS, row.errorKind)}</td>
                    <td className="px-4 py-2 text-right font-medium text-red-700">{row.count}</td>
                  </tr>
                ))}
                {!stats.byErrorKind.length && (
                  <tr><td className="px-4 py-6 text-center text-gray-500">Nenhuma falha no período. 🎉</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="font-semibold text-gray-900 text-sm">
            Gerações {total > 0 && <span className="text-gray-500 font-normal">({offset + 1}–{Math.min(offset + limit, total)} de {total})</span>}
          </div>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg disabled:opacity-40 hover:bg-gray-200"
            >Anterior</button>
            <button
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
              className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg disabled:opacity-40 hover:bg-gray-200"
            >Próxima</button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 animate-pulse space-y-3">
            {[...Array(6)].map((_, i) => <div key={i} className="h-6 bg-gray-100 rounded" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium whitespace-nowrap">Início</th>
                  <th className="text-left px-3 py-2 font-medium">Empresa</th>
                  <th className="text-left px-3 py-2 font-medium">Área / Operação</th>
                  <th className="text-left px-3 py-2 font-medium">Plataforma · Modelo</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Tent.</th>
                  <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Esperou</th>
                  <th className="text-right px-3 py-2 font-medium whitespace-nowrap" title="Tokens de entrada servidos pelo cache de prompt do provedor">Cache</th>
                  <th className="text-left px-4 py-2 font-medium">Erro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(row => (
                  <tr
                    key={row.id}
                    onClick={() => openDetail(row.id)}
                    className={`cursor-pointer hover:bg-blue-50 ${row.status === 'failed' ? 'bg-red-50/40' : ''}`}
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-gray-600">{formatDateTime(row.startedAt)}</td>
                    <td className="px-3 py-2 text-gray-700">{row.tenantName || row.tenantId || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="text-gray-900">{KIND_ICONS[row.kind] || ''} {row.operation}</div>
                      <div className="text-xs text-gray-500">
                        {labelFor(SURFACE_LABELS, row.surface)}
                        {row.label ? ` · ${row.label.slice(0, 60)}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-gray-900">{labelFor(PROVIDER_LABELS, row.provider)}</div>
                      <div className="text-xs text-gray-500">{row.model || '—'}</div>
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={row.status} /></td>
                    <td className={`px-3 py-2 text-right ${row.attempts > 1 ? 'text-amber-700 font-medium' : 'text-gray-600'}`}>
                      {row.attempts}/{row.maxAttempts}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                      {formatDuration(row.totalMs ?? (row.status === 'running' ? Date.now() - new Date(row.startedAt).getTime() : null))}
                      {row.waitMs ? <div className="text-xs text-gray-400">+{formatDuration(row.waitMs)} espera</div> : null}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {row.cache ? (
                        <div className={row.cache.hitRatio >= 0.5 ? 'text-emerald-700' : row.cache.cachedTokens > 0 ? 'text-amber-700' : 'text-gray-400'}>
                          {Math.round(row.cache.hitRatio * 100)}%
                          <div className="text-xs text-gray-400">{row.cache.cachedTokens.toLocaleString('pt-BR')}/{row.cache.inputTokens.toLocaleString('pt-BR')}</div>
                        </div>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2 max-w-md">
                      {row.status === 'failed' ? (
                        <div>
                          <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            {labelFor(ERROR_KIND_LABELS, row.errorKind)}{row.httpStatus ? ` · ${row.httpStatus}` : ''}
                          </span>
                          <div className="text-xs text-gray-600 mt-1 line-clamp-2">{row.errorMessage?.slice(0, 200)}</div>
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">Nenhuma geração encontrada com esses filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detalhe */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setDetail(null)}>
          <div className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {KIND_ICONS[detail.kind] || ''} {detail.operation || 'Detalhe da geração'}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">{detail.label || labelFor(SURFACE_LABELS, detail.surface)}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>

            {detailLoading ? (
              <div className="p-6 animate-pulse space-y-3">
                {[...Array(8)].map((_, i) => <div key={i} className="h-5 bg-gray-100 rounded" />)}
              </div>
            ) : (
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  {([
                    ['Status', <StatusBadge status={detail.status} />],
                    ['Área', labelFor(SURFACE_LABELS, detail.surface)],
                    ['Plataforma', labelFor(PROVIDER_LABELS, detail.provider)],
                    ['Modelo', detail.model || '—'],
                    ['Empresa', detail.tenantName || detail.tenantId || '—'],
                    ['Tipo', detail.kind],
                    ['Tentativas', `${detail.attempts} de ${detail.maxAttempts}`],
                    ['Timeout configurado', formatDuration(detail.timeoutMs)],
                    ['Tempo total (parede)', formatDuration(detail.totalMs)],
                    ['Tempo no provedor', formatDuration(detail.providerMs)],
                    ['Espera entre tentativas', formatDuration(detail.waitMs)],
                    ['Cache de prompt', detail.resultJson?.cache
                      ? `${Math.round(detail.resultJson.cache.hitRatio * 100)}% (${detail.resultJson.cache.cachedTokens}/${detail.resultJson.cache.inputTokens} tokens de entrada)`
                      : '—'],
                    ['HTTP status', detail.httpStatus ?? '—'],
                    ['Início', formatDateTime(detail.startedAt)],
                    ['Fim', formatDateTime(detail.finishedAt)],
                    ['Job', detail.jobId ? `${detail.jobType || 'job'} · ${detail.jobId}` : '—'],
                    ['Etapa', detail.stepId || '—'],
                  ] as [string, any][]).map(([label, value]) => (
                    <div key={label}>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
                      <div className="text-gray-900 mt-0.5 break-words">{value}</div>
                    </div>
                  ))}
                </div>

                {detail.status === 'failed' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-sm font-semibold text-red-900">
                      {labelFor(ERROR_KIND_LABELS, detail.errorKind)}
                      {detail.retryable != null && (
                        <span className="ml-2 text-xs font-normal text-red-700">
                          ({detail.retryable ? 'transitório — foi retentado' : 'determinístico — não faz sentido retentar'})
                        </span>
                      )}
                    </div>
                    <pre className="text-xs text-red-900 whitespace-pre-wrap mt-2 font-mono">{detail.errorMessage}</pre>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Tentativas</h4>
                  <div className="space-y-2">
                    {(detail.attemptsJson as AttemptEntry[] || []).map(attempt => (
                      <div key={attempt.n} className={`border rounded-lg p-3 ${attempt.status === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-900">
                            Tentativa {attempt.n} · {attempt.status === 'success' ? 'sucesso' : labelFor(ERROR_KIND_LABELS, attempt.errorKind)}
                            {attempt.httpStatus ? ` · HTTP ${attempt.httpStatus}` : ''}
                          </span>
                          <span className="text-gray-600">
                            {formatDuration(attempt.durationMs)}
                            {attempt.waitAfterMs ? ` · esperou ${formatDuration(attempt.waitAfterMs)}` : ''}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{formatDateTime(attempt.startedAt)}</div>
                        {attempt.error && (
                          <pre className="text-xs text-gray-800 whitespace-pre-wrap mt-2 font-mono max-h-60 overflow-y-auto">{attempt.error}</pre>
                        )}
                      </div>
                    ))}
                    {!(detail.attemptsJson || []).length && (
                      <div className="text-sm text-gray-500">Nenhuma tentativa registrada (geração aberta e interrompida antes da primeira chamada).</div>
                    )}
                  </div>
                </div>

                {detail.requestJson && Object.keys(detail.requestJson).length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Requisição</h4>
                    <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap font-mono max-h-80 overflow-y-auto">
                      {JSON.stringify(detail.requestJson, null, 2)}
                    </pre>
                  </div>
                )}

                {detail.resultJson && Object.keys(detail.resultJson).length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Resultado</h4>
                    <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap font-mono max-h-60 overflow-y-auto">
                      {JSON.stringify(detail.resultJson, null, 2)}
                    </pre>
                  </div>
                )}

                <div className="text-xs text-gray-400 font-mono">ID do log: {detail.id}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
